import { useEffect, useMemo, useRef, useState } from "react";
import { ModalContainer, ModalBody, ModalHeader, ModalFooter } from "./Modal";
import { useSearchParams } from "react-router";
import Input from "./Input";
import useUser from "@app/zustand/user";
import { RoomUpdateResponse } from "@api/src/types/responses";
import { refreshUser } from "@app/utils/use-most-fresh-user";

export default function EditGroupName({ className = "" }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const me = useUser((state) => state.user!);
  const roomId = searchParams.get("qRoomId");
  const roomRelation = useMemo(() => me.rooms.find((r) => r.room.id === roomId), [me.rooms, roomId]);
  const [name, setName] = useState(roomRelation?.room.manualName);
  const forceOpen = useRef(false);

  useEffect(() => {
    if (!name && !forceOpen.current) {
      const numberOfUsers = roomRelation?.room.numberOfUsers ?? 2;
      if (numberOfUsers > 3) {
        forceOpen.current = true;
        setOpen(true);
      }
    }
  }, [name, roomRelation]);

  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        Modifier le nom du groupe
      </button>
      <ModalContainer open={open} className="flex-col">
        <ModalHeader
          title={forceOpen.current ? "Donner un nom au groupe" : "Modifier le nom du groupe"}
          onClose={() => setOpen(false)}
        />
        <ModalBody>
          <form
            id={`edit-group-name-${roomId}`}
            method="POST"
            className="flex flex-col gap-y-2 px-4"
            onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              setLoading(true);
              fetch(`${import.meta.env.VITE_API_URL}/room/${roomId}`, {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  manualName: name,
                }),
              })
                .then((response) => response.json() as Promise<RoomUpdateResponse>)
                .then((response) => {
                  if (response.ok) {
                    setOpen(false);
                    refreshUser("edit relation");
                    forceOpen.current = false;
                  } else if (response.error) {
                    alert(response.error);
                  }
                })
                .finally(() => {
                  setLoading(false);
                });
            }}
          >
            {forceOpen.current && (
              <p className="text-sm text-gray-500 p-2">
                Vous commences à être nombreux, donnez un nom au groupe pour simplifier la discussion.
              </p>
            )}
            <Input
              // label="Nom du groupe"
              name="name"
              value={name}
              required
              type="text"
              placeholder="Nom du groupe"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </form>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="inline-block rounded-lg border border-sky-800 bg-white px-5 py-1 text-sky-800"
            onClick={() => setOpen(false)}
          >
            Annuler
          </button>
          <button
            type="submit"
            form={`edit-group-name-${roomId}`}
            className="inline-block rounded-lg border-none bg-sky-800 px-5 py-1 text-white hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "En cours..." : "Enregistrer"}
          </button>
        </ModalFooter>
      </ModalContainer>
    </>
  );
}
